import React, { ReactElement, useState } from 'react';
import { Accordion, OverlayTrigger, Popover } from 'react-bootstrap';
import classnames from 'classnames';
import angleDown from 'v2/icons/angleDown.svg';
import angleUp from 'v2/icons/angleUp.svg';
import styles from 'v2/common/Activity/activityAssociations.module.scss';

interface AssociationsLayoutProps {
  header: string;
  content: ReactElement;
  onShow?: () => void;
}

interface AccordionLayoutProps extends AssociationsLayoutProps {
  customStyle?: {
    readonly [key: string]: string;
  };
  openByDefault?: boolean;
}

export type AssociationsLayout = (
  props: AssociationsLayoutProps
) => ReactElement;

export const AccordionLayout = ({
  header,
  content,
  onShow,
  customStyle,
  openByDefault = false,
}: AccordionLayoutProps) => {
  const currentStyles = customStyle ?? styles;
  const [isCollapsed, setIsCollapsed] = useState(!openByDefault);
  const handleClick = () => {
    if (isCollapsed) {
      onShow?.();
    }
    setIsCollapsed(!isCollapsed);
  };

  return (
    <Accordion
      className={currentStyles.accordionContainer}
      defaultActiveKey={openByDefault ? '0' : undefined}
    >
      <Accordion.Toggle
        className={classnames(currentStyles.headerTitle, currentStyles.header)}
        eventKey="0"
        as="div"
        data-cy="associations:accordion"
        onClick={handleClick}
      >
        <span className={currentStyles.title}>{header}</span>
        <img
          src={isCollapsed ? angleDown : angleUp}
          alt={isCollapsed ? 'arrowDownIcon' : 'arrowUpIcon'}
          className={currentStyles.arrow}
        />
      </Accordion.Toggle>
      <Accordion.Collapse eventKey="0" data-testid="collapse_menu">
        {content}
      </Accordion.Collapse>
    </Accordion>
  );
};

export const OverlayLayout = ({
  header,
  content,
  onShow,
}: AssociationsLayoutProps) => {
  const [isShow, setIsShow] = useState(false);

  const handleClose = () => {
    setIsShow(false);
  };

  const handleShow = () => {
    onShow?.();
    setIsShow(true);
  };

  const popover = (
    <Popover
      id="activity_associations_popover"
      className={classnames(styles.popover)}
    >
      <Popover.Content>{content}</Popover.Content>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      placement="bottom-start"
      overlay={popover}
      rootClose={true}
      onEnter={handleShow}
      onExited={handleClose}
    >
      <div
        className={classnames(styles.popoverTitle)}
        data-cy="associations:count"
      >
        {header}
        <img
          src={isShow ? angleUp : angleDown}
          alt={isShow ? 'arrowUpIcon' : 'arrowDownIcon'}
          className={styles.arrow}
        />
      </div>
    </OverlayTrigger>
  );
};
